import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent } from '@components';
import { useToast } from '../../../contexts/useToast';
import { CompanyInfoModal } from '../components/CompanyInfoModal';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import { FaqEntryStatus } from '../components/FaqEntryStatus';
import { useCompanyInfo, useSaveCompanyInfo } from '../hooks';
import { COMPANY_INFO_TOPICS, CompanyInfoTopic, FaqEntry, KnowledgeBase } from '../types';

interface CompanyInfoTabProps {
  knowledgeBase: KnowledgeBase;
}

export const CompanyInfoTab: React.FC<CompanyInfoTabProps> = ({ knowledgeBase }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { data: entries = [], isLoading, isError } = useCompanyInfo(knowledgeBase.id);
  const saveCompanyInfo = useSaveCompanyInfo(knowledgeBase.id);
  const [editingTopic, setEditingTopic] = useState<CompanyInfoTopic | null>(null);
  const [deletingTopic, setDeletingTopic] = useState<CompanyInfoTopic | null>(null);

  const entriesByTopic = Object.fromEntries(
    entries.map(entry => [entry.company_info_topic, entry])
  ) as Partial<Record<CompanyInfoTopic, FaqEntry>>;
  const filledTopics = COMPANY_INFO_TOPICS.filter(topic => entriesByTopic[topic]);
  const missingTopics = COMPANY_INFO_TOPICS.filter(topic => !entriesByTopic[topic]);
  const label = (topic: CompanyInfoTopic) => t(`knowledgeBase.companyInfo.topics.${topic}.label`);

  const save = (topic: CompanyInfoTopic, answer: string) =>
    saveCompanyInfo.mutateAsync({ [topic]: answer });

  const handleSubmit = async (topic: CompanyInfoTopic, answer: string) => {
    await save(topic, answer);
    showToast({ type: 'success', message: t('knowledgeBase.companyInfo.saved') });
  };

  const handleDelete = async () => {
    if (!deletingTopic) return;
    try {
      await save(deletingTopic, '');
      showToast({ type: 'success', message: t('knowledgeBase.companyInfo.deleted') });
      setDeletingTopic(null);
    } catch {
      showToast({ type: 'error', message: t('knowledgeBase.companyInfo.deleteFailed') });
    }
  };

  // Saving the unchanged answer again restarts the processing of a failed entry
  const handleRetry = async (entry: FaqEntry & { company_info_topic: CompanyInfoTopic }) => {
    try {
      await save(entry.company_info_topic, entry.answer);
    } catch {
      showToast({ type: 'error', message: t('knowledgeBase.faq.processFailed') });
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('knowledgeBase.companyInfo.title')}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {t('knowledgeBase.companyInfo.description')}
        </p>
      </div>

      {missingTopics.length > 0 && !isLoading && !isError && (
        <div className="flex flex-wrap gap-2">
          {missingTopics.map(topic => (
            <Button
              key={topic}
              variant="outline"
              size="sm"
              onClick={() => setEditingTopic(topic)}
              className="flex items-center gap-1"
            >
              <span className="material-icons text-base">add</span>
              {label(topic)}
            </Button>
          ))}
        </div>
      )}

      <Card>
        <CardContent>
          {isError ? (
            <p className="py-8 text-center text-red-500">
              {t('knowledgeBase.companyInfo.loadFailed')}
            </p>
          ) : isLoading ? (
            <p className="py-8 text-center text-gray-500">{t('common.loading')}</p>
          ) : filledTopics.length === 0 ? (
            <div className="py-12 text-center space-y-2">
              <span className="material-icons text-3xl text-gray-400">business</span>
              <p className="text-gray-600 dark:text-gray-400">
                {t('knowledgeBase.companyInfo.empty')}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {filledTopics.map(topic => {
                const entry = entriesByTopic[topic] as FaqEntry & {
                  company_info_topic: CompanyInfoTopic;
                };
                return (
                  <CompanyInfoRow
                    key={topic}
                    label={label(topic)}
                    entry={entry}
                    onEdit={() => setEditingTopic(topic)}
                    onDelete={() => setDeletingTopic(topic)}
                    onRetry={() => handleRetry(entry)}
                    isRetrying={saveCompanyInfo.isPending}
                  />
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <CompanyInfoModal
        topic={editingTopic}
        answer={editingTopic ? (entriesByTopic[editingTopic]?.answer ?? '') : ''}
        onClose={() => setEditingTopic(null)}
        onSubmit={handleSubmit}
        isSaving={saveCompanyInfo.isPending}
      />

      <ConfirmDeleteModal
        isOpen={deletingTopic !== null}
        onClose={() => setDeletingTopic(null)}
        onConfirm={handleDelete}
        title={t('knowledgeBase.companyInfo.deleteEntry')}
        question={t('knowledgeBase.companyInfo.confirmDelete')}
        itemName={deletingTopic ? label(deletingTopic) : ''}
        warning={t('knowledgeBase.companyInfo.deleteWarning')}
        confirmLabel={t('knowledgeBase.companyInfo.deleteEntry')}
        isDeleting={saveCompanyInfo.isPending}
      />
    </div>
  );
};

interface CompanyInfoRowProps {
  label: string;
  entry: FaqEntry;
  onEdit: () => void;
  onDelete: () => void;
  onRetry: () => void;
  isRetrying: boolean;
}

const CompanyInfoRow: React.FC<CompanyInfoRowProps> = ({
  label,
  entry,
  onEdit,
  onDelete,
  onRetry,
  isRetrying,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <li className="py-4 space-y-2">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <p className="font-medium text-gray-900 dark:text-white">{label}</p>
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className={`text-left text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line break-words ${
              isExpanded ? '' : 'line-clamp-2'
            }`}
            title={isExpanded ? t('knowledgeBase.faq.showLess') : t('knowledgeBase.faq.showMore')}
          >
            {entry.answer}
          </button>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            className="p-1.5 h-8 w-8"
            title={t('knowledgeBase.companyInfo.editEntry')}
          >
            <span className="material-icons text-base text-gray-600 dark:text-gray-400">edit</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="p-1.5 h-8 w-8"
            title={t('knowledgeBase.companyInfo.deleteEntry')}
          >
            <span className="material-icons text-base text-red-600">delete</span>
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <FaqEntryStatus entry={entry} onRetry={onRetry} isRetrying={isRetrying} />
      </div>
    </li>
  );
};
